import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Layers, Image as ImageIcon, Layout, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { serviceSchema, faqSchema as toFaqSchema } from "@/data/schema";
import CtaBanner from "@/components/CtaBanner";

export default function APlusListingGraphics() {
  const schema = serviceSchema({
    name: "A+ Content & Listing Graphics Design Services",
    description: "A+ Content & Listing Graphics Design Services from Orina Enterprise for marketplace and D2C brands.",
    path: "/services/a-plus-listing-graphics",
    serviceType: "A+ content design",
  });
  const faqs = [
    { q: "Do I need Amazon Brand Registry to publish A+ content?", a: "Yes. Brand Registry is a prerequisite for Amazon A+ content." },
    { q: "How many modules per SKU?", a: "5-7 modules is the sweet spot for mobile-first A+ content." },
    { q: "How long does A+ content design take?", a: "Typically 7-10 working days per SKU family including revisions." },
    { q: "Can you produce Flipkart Rich Content and Nykaa Brand Pages too?", a: "Yes. We adapt the creative for each marketplace's aspect ratios and design system." }
  ];
  const faqSchema = toFaqSchema(faqs);

  return (
    <>
      <SEO
        title="A+ Listing Graphics & E-commerce Product Design Services India"
        description="A+ content graphics, product infographics, comparison charts, lifestyle creatives and marketplace image sets — mobile-first, on-brand, 7-10 day turnaround."
        keywords="A+ content design services, ecommerce product listing graphics, Amazon A+ content, product infographic design"
        path="/services/a-plus-listing-graphics"
        schema={[schema, faqSchema]}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "A+ Listing Graphics", path: "/services/a-plus-listing-graphics" }]}
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">Creative Services</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
            A+ Listing Graphics That Help Your Products Sell Better
          </h1>
          <p className="mt-5 max-w-3xl text-base text-black/85 md:text-lg">
            Mobile-first A+ content, product infographics, comparison charts, ingredient grids, benefit icons and lifestyle creatives — built for Amazon, Flipkart, Meesho, JioMart, Myntra, Nykaa and quick-commerce marketplaces.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact/?type=expert" className="btn-brutal">Talk to a design expert <ArrowUpRight size={16} /></Link>
            <Link to="/blog/a-plus-content-and-product-graphics-guide/" className="btn-brutal secondary">Read the A+ content guide</Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">What we create</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { i: <Layers size={22} />, t: "A+ content modules", d: "Brand banners, feature grids, ingredient grids, comparison tables, FAQ blocks." },
              { i: <ImageIcon size={22} />, t: "Product infographics", d: "Icon-driven feature and benefit callouts, mobile-optimised." },
              { i: <Layout size={22} />, t: "Lifestyle creatives", d: "Real-world context shots that reduce regret returns." },
              { i: <Sparkles size={22} />, t: "Comparison charts", d: "Your SKU family or your product vs generic alternatives." },
              { i: <ImageIcon size={22} />, t: "Marketplace image sets", d: "Main + lifestyle + infographic + comparison + scale + packaging + video." },
              { i: <Layers size={22} />, t: "Brand Page & Store banners", d: "Amazon Brand Store, Flipkart Brand Page, Nykaa Brand Page." }
            ].map((s) => (
              <div key={s.t} className="card-brutal">
                <div className="flex h-11 w-11 items-center justify-center border-2 border-black bg-black text-[#FFD700]">{s.i}</div>
                <h3 className="display mt-4 text-lg font-black">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-black/75">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="eyebrow">A+ content workflow</div>
          <h2 className="display mt-3 text-3xl font-black md:text-4xl">How we design your A+ content</h2>
          <ol className="mt-8 space-y-4">
            {[
              "Brief: product spec, customer, primary keywords, competitor review analysis",
              "Storyboard: module-by-module blueprint with copy and thumbnails",
              "Photography: product-on-white, lifestyle and hero angle shots",
              "Design: mobile-first mockups of each module",
              "Copy: benefit-led captions written for scannability",
              "QA: mobile preview, micro-typography, compression check",
              "Publish and A/B test"
            ].map((s, i) => (
              <li key={s} className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center border-2 border-black bg-[#FFD700] text-sm font-black">{i + 1}</div>
                <span className="pt-1 text-base md:text-lg">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">What's included</h2>
          <ul className="mt-6 grid gap-3 md:grid-cols-2">
            {[
              "5-7 A+ modules per SKU family",
              "8 marketplace image slots per SKU (main + 7)",
              "Mobile-first layouts",
              "Storyboarding + 2 revision rounds",
              "Amazon Brand Registry-ready workflow",
              "Flipkart Rich Content and Nykaa Brand Page adaptation",
              "Copywriting for every caption",
              "Publish-ready file delivery"
            ].map((o) => (
              <li key={o} className="flex items-start gap-2"><CheckCircle2 className="mt-1 shrink-0 text-[#25D366]" size={18} />{o}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">FAQ</h2>
          <div className="mt-8 space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="card-brutal">
                <summary className="cursor-pointer text-base font-bold md:text-lg">{f.q}</summary>
                <p className="mt-3 text-sm text-black/80 md:text-base">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner title="Need A+ content that converts?" subtitle="Free 30-minute creative audit. We'll show you exactly what to fix on your existing listings." />
    </>
  );
}
